import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  loginUser,
  registerUser,
  getAllCategories,
  createBlogPost,
} from './../../Api';
import type { UserProfile, RegisteredUser, Category } from './../../Api';

export default function CitizenSubmission() {
  const [currentUser, setCurrentUser] = useState<UserProfile | RegisteredUser | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [activeTab, setActiveTab] = useState<'article' | 'video' | 'photo'>('article');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [signUpError, setSignUpError] = useState<string | null>(null);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string>('');

  // File Upload State
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Dynamic Categories State
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  const [loginForm, setLoginForm] = useState({
    phoneNumber: '',
    password: ''
  });

  const [signUpForm, setSignUpForm] = useState({
    name: '',
    phoneNumber: '',
    role: 'user',
    password: '',
    confirmPassword: ''
  });

  const [submissionForm, setSubmissionForm] = useState({
    title: '',
    description: '',
    location: '',
    categoryId: '',
    mediaUrl: 'https://example.com/image.jpg'
  });

  const navigate = useNavigate();

  // Reset file selection when switching tabs
  const handleTabChange = (tab: 'article' | 'video' | 'photo') => {
    setActiveTab(tab);
    setSelectedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle file selection and generate local preview
  const handleFileChange = (file: File | undefined) => {
    if (!file) return;

    if (activeTab === 'video' && !file.type.startsWith('video/')) {
      alert('Please upload a valid video file.');
      return;
    }

    if ((activeTab === 'article' || activeTab === 'photo') && !file.type.startsWith('image/')) {
      alert('Please upload a valid image file.');
      return;
    }

    setSelectedFile(file);
    const previewUrl = URL.createObjectURL(file);
    setFilePreview(previewUrl);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  // Fetch categories dynamically when logged in
  useEffect(() => {
    if (isLoggedIn) {
      const fetchCategories = async () => {
        setIsLoadingCategories(true);
        const result = await getAllCategories();
        if (result.success && result.data?.categories) {
          setCategories(result.data.categories);
          if (result.data.categories.length > 0 && !submissionForm.categoryId) {
            setSubmissionForm((prev) => ({
              ...prev,
              categoryId: result.data!.categories[0].id.toString()
            }));
          }
        }
        setIsLoadingCategories(false);
      };

      fetchCategories();
    }
  }, [isLoggedIn]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setIsLoggingIn(true);

    try {
      const result = await loginUser({
        phoneNumber: loginForm.phoneNumber.trim(),
        password: loginForm.password
      });

      if (result.success && result.data) {
        setCurrentUser(result.data.user);
        setIsLoggedIn(true);
        setLoginForm({ phoneNumber: '', password: '' });
      } else {
        setLoginError(result.error || 'Invalid credentials. Please try again.');
      }
    } catch (err: any) {
      setLoginError(err.message || 'An unexpected error occurred during login.');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignUpError(null);

    if (signUpForm.password !== signUpForm.confirmPassword) {
      setSignUpError('Passwords do not match');
      return;
    }

    if (!signUpForm.name.trim() || !signUpForm.phoneNumber.trim() || !signUpForm.password) {
      setSignUpError('Please fill in all required fields');
      return;
    }

    setIsSigningUp(true);

    try {
      const result = await registerUser({
        name: signUpForm.name.trim(),
        phoneNumber: signUpForm.phoneNumber.trim(),
        role: signUpForm.role,
        password: signUpForm.password
      });

      if (result.success && result.data) {
        setCurrentUser(result.data.user);
        setIsLoggedIn(true);
        setShowSignUp(false);
        setSignUpForm({
          name: '',
          phoneNumber: '',
          role: 'user',
          password: '',
          confirmPassword: ''
        });
      } else {
        setSignUpError(result.error || 'Failed to create account. Please try again.');
      }
    } catch (err: any) {
      setSignUpError(err.message || 'An unexpected error occurred.');
    } finally {
      setIsSigningUp(false);
    }
  };

  const handleSubmission = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitErrorMessage('');

    // Validate textarea character limit
    if (submissionForm.description.length > 500) {
      alert('Description must be 500 characters or less');
      setIsSubmitting(false);
      return;
    }

    // Determine media_type dynamically based on the active tab
    let mediaType: 'text' | 'video' | 'image' = 'text';
    if (activeTab === 'video') {
      mediaType = 'video';
    } else if (activeTab === 'photo') {
      mediaType = 'image';
    }

    // Resolve category ID dynamically
    const selectedCategoryId = submissionForm.categoryId
      ? parseInt(submissionForm.categoryId, 10)
      : categories.length > 0
      ? categories[0].id
      : 1;

    try {
      const response = await createBlogPost({
        title: submissionForm.title.trim(),
        content: submissionForm.description.trim(),
        media_type: mediaType,
        media_url: submissionForm.mediaUrl || 'https://example.com/image.jpg',
        location: submissionForm.location.trim(),
        category_id: selectedCategoryId,
        tags: ['news', 'community'],
        status: 'draft'
      });

      if (response.success) {
        const storedPosts = JSON.parse(localStorage.getItem('citizen_submission_posts') || '[]');
        const newPost = {
          id: Date.now(),
          title: submissionForm.title.trim(),
          content: submissionForm.description.trim(),
          media_type: mediaType,
          media_url: submissionForm.mediaUrl || 'https://example.com/image.jpg',
          location: submissionForm.location.trim(),
          category_id: selectedCategoryId,
          tags: ['news', 'community'],
          status: 'Pending',
          author_id: currentUser?.id || 0,
          author_name: currentUser?.name || 'Contributor',
          category_name: categories.find((category) => category.id === selectedCategoryId)?.name || 'General',
          createdAt: new Date().toISOString(),
        };

        localStorage.setItem('citizen_submission_posts', JSON.stringify([newPost, ...storedPosts]));

        setSubmitStatus('success');
        // Reset form and file state
        setSubmissionForm({
          title: '',
          description: '',
          location: '',
          categoryId: categories.length > 0 ? categories[0].id.toString() : '',
          mediaUrl: 'https://example.com/image.jpg'
        });
        setSelectedFile(null);
        setFilePreview(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setSubmitStatus('error');
        setSubmitErrorMessage(response.error || 'Submission failed. Please try again.');
      }
    } catch (error: any) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
      setSubmitErrorMessage(error.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center py-12 px-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
            <p className="text-gray-600">Sign in to share your stories</p>
          </div>

          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-6">
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                value={loginForm.phoneNumber}
                onChange={(e) => setLoginForm({...loginForm, phoneNumber: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Enter your phone number"
                required
                disabled={isLoggingIn}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Enter your password"
                required
                disabled={isLoggingIn}
              />
            </div>

            <button
              type="submit"
              disabled={isLoggingIn}
              className="w-full bg-red-700 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-800 transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoggingIn ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <button className="mt-4 w-full bg-white border border-gray-300 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-50 transition-colors duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer">
              <i className="ri-google-fill text-xl text-red-500"></i>
              Sign in with Google
            </button>
          </div>

          <p className="mt-6 text-center text-sm text-gray-600">
            Don't have an account?{' '}
            <button 
              onClick={() => {
                setSignUpError(null);
                setShowSignUp(true);
              }}
              className="text-red-700 hover:text-red-800 font-medium cursor-pointer"
            >
              Sign up here
            </button>
          </p>
        </div>

        {/* Sign Up Modal */}
        {showSignUp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-6 z-50">
            <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Create Account</h2>
                <button
                  onClick={() => setShowSignUp(false)}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {signUpError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
                  {signUpError}
                </div>
              )}

              <form onSubmit={handleSignUp} className="space-y-4">
                <div>
                  <label htmlFor="signup-name" className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="signup-name"
                    value={signUpForm.name}
                    onChange={(e) => setSignUpForm({...signUpForm, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="signup-phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="signup-phone"
                    value={signUpForm.phoneNumber}
                    onChange={(e) => setSignUpForm({...signUpForm, phoneNumber: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="e.g. 9876543211"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="signup-role" className="block text-sm font-medium text-gray-700 mb-2">
                    Role *
                  </label>
                  <select
                    id="signup-role"
                    value={signUpForm.role}
                    onChange={(e) => setSignUpForm({...signUpForm, role: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-gray-100 cursor-not-allowed text-gray-600"
                    disabled
                  >
                    <option value="user">User</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="signup-password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    id="signup-password"
                    value={signUpForm.password}
                    onChange={(e) => setSignUpForm({...signUpForm, password: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Create a password"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="signup-confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    id="signup-confirm-password"
                    value={signUpForm.confirmPassword}
                    onChange={(e) => setSignUpForm({...signUpForm, confirmPassword: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Confirm your password"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSigningUp}
                  className="w-full bg-red-700 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-800 transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSigningUp ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-600">
                Already have an account?{' '}
                <button 
                  onClick={() => setShowSignUp(false)}
                  className="text-red-700 hover:text-red-800 font-medium cursor-pointer"
                >
                  Sign in here
                </button>
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Hi {currentUser?.name || 'Contributor'}, Share what's happening around you!
          </h1>
          <p className="text-xl text-gray-600">
            Your stories help build a stronger community
          </p>

          {String(currentUser?.role || '').toLowerCase() === 'admin' && (
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => navigate('/citizen-submission/viewContent')}
                className="bg-red-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                View content
              </button>
            </div>
          )}
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Notification Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
            <div className="flex items-center gap-3">
              <i className="ri-information-line text-blue-600 text-xl"></i>
              <p className="text-blue-800">
                Your story will be verified by our editors before publishing.
              </p>
            </div>
          </div>

          {/* Status Messages */}
          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
              <div className="flex items-center gap-3">
                <i className="ri-check-line text-green-600 text-xl"></i>
                <p className="text-green-800">Blog post created and submitted successfully for review!</p>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
              <div className="flex items-center gap-3">
                <i className="ri-error-warning-line text-red-600 text-xl"></i>
                <p className="text-red-800">{submitErrorMessage || 'Submission failed. Please try again.'}</p>
              </div>
            </div>
          )}

          {/* Upload Tabs */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex">
                <button
                  onClick={() => handleTabChange('article')}
                  className={`flex-1 py-4 px-6 text-center font-medium transition-colors duration-300 cursor-pointer ${
                    activeTab === 'article'
                      ? 'bg-red-700 text-white'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-article-line text-xl mr-2"></i>
                  Article
                </button>
                <button
                  onClick={() => handleTabChange('video')}
                  className={`flex-1 py-4 px-6 text-center font-medium transition-colors duration-300 cursor-pointer ${
                    activeTab === 'video'
                      ? 'bg-red-700 text-white'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-video-line text-xl mr-2"></i>
                  Video
                </button>
                <button
                  onClick={() => handleTabChange('photo')}
                  className={`flex-1 py-4 px-6 text-center font-medium transition-colors duration-300 cursor-pointer ${
                    activeTab === 'photo'
                      ? 'bg-red-700 text-white'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <i className="ri-image-line text-xl mr-2"></i>
                  Photo Story
                </button>
              </nav>
            </div>

            <div className="p-8">
              <form 
                id="citizen-submission-form"
                onSubmit={handleSubmission} 
                className="space-y-6"
              >
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={submissionForm.title}
                    onChange={(e) => setSubmissionForm({...submissionForm, title: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Give your story a compelling title"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                    Description * (Max 500 characters)
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={submissionForm.description}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) {
                        setSubmissionForm({...submissionForm, description: e.target.value});
                      }
                    }}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none text-sm"
                    placeholder="Tell your story in detail..."
                    required
                    disabled={isSubmitting}
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {submissionForm.description.length}/500 characters
                  </div>
                </div>

                {/* Upload Area */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload {activeTab === 'article' ? 'Image' : activeTab === 'video' ? 'Video' : 'Photos'} *
                  </label>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={(e) => handleFileChange(e.target.files?.[0])}
                    accept={activeTab === 'video' ? 'video/*' : 'image/*'}
                    className="hidden"
                  />
                  <div 
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-400 transition-colors duration-300 cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {filePreview ? (
                      <div className="flex flex-col items-center justify-center">
                        {activeTab === 'video' ? (
                          <video 
                            src={filePreview} 
                            controls 
                            className="max-h-56 rounded-lg mb-3 shadow-md"
                            onClick={(e) => e.stopPropagation()}
                          />
                        ) : (
                          <img 
                            src={filePreview} 
                            alt="Preview" 
                            className="max-h-56 rounded-lg mb-3 object-contain shadow-md"
                          />
                        )}
                        <p className="text-sm font-medium text-gray-700 mb-2">{selectedFile?.name}</p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(null);
                            setFilePreview(null);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                          className="text-xs text-red-600 hover:text-red-800 underline"
                        >
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <>
                        <i className={`${activeTab === 'video' ? 'ri-video-upload-line' : 'ri-image-add-line'} text-4xl text-gray-400 mb-4`}></i>
                        <p className="text-gray-600 mb-2">
                          Drag and drop your {activeTab === 'article' ? 'image' : activeTab === 'video' ? 'video' : 'photos'} here, or click to browse
                        </p>
                        <p className="text-sm text-gray-500 mb-4">
                          {activeTab === 'video' ? 'Supports MP4, WebM, MOV' : 'Supports JPG, PNG, WebP'}
                        </p>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            fileInputRef.current?.click();
                          }}
                          className="bg-red-700 text-white px-6 py-2 rounded-lg hover:bg-red-800 transition-colors duration-300 whitespace-nowrap cursor-pointer"
                          disabled={isSubmitting}
                        >
                          Choose File
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={submissionForm.location}
                      onChange={(e) => setSubmissionForm({...submissionForm, location: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                      placeholder="Where did this happen?"
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-2">
                      Category *
                    </label>
                    <select
                      id="category"
                      name="category"
                      value={submissionForm.categoryId}
                      onChange={(e) => setSubmissionForm({...submissionForm, categoryId: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent pr-8 text-sm bg-white"
                      disabled={isSubmitting || isLoadingCategories}
                      required
                    >
                      {isLoadingCategories ? (
                        <option value="">Loading categories...</option>
                      ) : categories.length > 0 ? (
                        categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))
                      ) : (
                        <option value="">No categories available</option>
                      )}
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-red-700 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-red-800 transition-colors duration-300 whitespace-nowrap cursor-pointer disabled:bg-gray-400 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>
                      Submitting...
                    </span>
                  ) : (
                    'Submit for Review'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}